import {
  useAddArtworksToEnsembles,
  useCreateTemporarySetWithArtworks,
  useGetArtworkSets,
  useRemoveArtworksFromEnsembles,
} from "@/api/ensemble/ensemble";
import { toast } from "@/hooks/use-toast";
import { Ensemble } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { Minus, Plus, Search } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import StyledDrawer from "../StyledDrawer";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import NewArtworkSetModal, {
  NewArtworkSetFormValues,
} from "./NewArtworkSetModal";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import { createActionsColumnDef } from "@/components/table/table-actions-column";

const visibilityToTranslationKey = (
  visibility: Ensemble["visibility"],
): string => {
  // In case translation keys or visibility values change
  switch (visibility) {
    case "private":
      return "private";
    case "shared":
      return "shared";
    default:
      return "";
  }
};

const visibilityToBadgeColor = (visibility: Ensemble["visibility"]): string => {
  switch (visibility) {
    case "private":
      return "bg-blue-50 border-blue-200 text-blue-700";
    case "shared":
      return "bg-green-50 border-green-200 text-green-700";
    default:
      return "";
  }
};

type ArtworkSetTableProps = {
  id?: string;
  data: Ensemble[];
  mode: "add" | "remove";
  onItemAction: (item: Ensemble) => void;
  disabled?: boolean;
};

const ArtworkSetTable = ({
  id,
  data,
  mode,
  onItemAction,
  disabled = false,
}: ArtworkSetTableProps) => {
  const { t } = useTranslation();
  const columns = useMemo<ColumnDef<Ensemble>[]>(
    () => [
      {
        id: "name",
        accessorKey: "ensemblesDescriptionNomEnsemble",
        header: t("ensemblesDescriptionNomEnsemble"),
        cell: (props) => (
          <span className="font-medium">{props.getValue<string>()}</span>
        ),
      },
      {
        id: "comment",
        accessorKey: "ensemblesDescriptionNotesEnsemble",
        header: t("ensemblesDescriptionNotesEnsemble"),
        cell: (props) => (
          <div className="max-w-76 text-wrap">{props.getValue<string>()}</div>
        ),
      },
      {
        id: "createdAt",
        accessorKey: "ensemblesDescriptionDateCreationEnsemble",
        header: t("createdAt"),
        cell: (props) => {
          const value = props.getValue<Date | undefined>();
          if (!value) return "";
          return new Date(value).toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        id: "artworkCount",
        accessorKey: "artworkCount",
        header: t("relatedArtworkCount"),
        cell: (props) => (
          <Badge className="bg-gray-500">{props.getValue<number>()}</Badge>
        ),
      },
      {
        id: "visibility",
        accessorKey: "visibility",
        header: t("visibility"),
        cell: (props) => {
          const value = props.getValue<Ensemble["visibility"]>();
          const key = visibilityToTranslationKey(value);

          if (!key) return null;
          return (
            <Badge variant="outlined" className={visibilityToBadgeColor(value)}>
              {t(key)}
            </Badge>
          );
        },
      },
      createActionsColumnDef<Ensemble>((cell) => {
        const ensemble = cell.row.original;
        const actions: TableRowAction[] =
          mode === "add"
            ? [
                {
                  id: "add",
                  label: t(ACTION_I18N_KEYS.add),
                  icon: Plus,
                  disabled,
                  onSelect: () => onItemAction(ensemble),
                },
              ]
            : [
                {
                  id: "remove",
                  label: t(ACTION_I18N_KEYS.delete),
                  icon: Minus,
                  destructive: true,
                  disabled,
                  onSelect: () => onItemAction(ensemble),
                },
              ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ],
    [t, onItemAction, mode, disabled],
  );

  return <DataTable columns={columns} data={data ?? []} id={id} />;
};

type CreateArtworkSetProps = {
  selectedArtworksIds: string[];
  resetSelectedArtworks: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
};

const CreateArtworkSet = ({
  selectedArtworksIds,
  resetSelectedArtworks,
  open,
  onOpenChange,
  disabled = false,
}: CreateArtworkSetProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>("");
  const { data: allSets } = useGetArtworkSets(
    { search },
    { placeholderData: (prev) => prev },
  );
  const { data: setsWithArtworks } = useGetArtworkSets(
    {
      withArtworksIds: selectedArtworksIds,
    },
    { enabled: !!selectedArtworksIds.length, placeholderData: (prev) => prev },
  );
  const allSetsId = useId();
  const setsWithArtworksId = useId();

  const [addedSets, setAddedSets] = useState<Ensemble[]>([]);
  const [removedSets, setRemovedSets] = useState<Ensemble[]>([]);

  const [newSetDialogOpen, setNewSetDialogOpen] = useState(false);

  const { mutateAsync: mutateAddArtworksToEnsembles } =
    useAddArtworksToEnsembles();
  const { mutateAsync: mutateRemoveArtworksFromEnsembles } =
    useRemoveArtworksFromEnsembles();
  const { mutateAsync: mutateCreateTemporarySetWithArtworks } =
    useCreateTemporarySetWithArtworks();

  const setsAddedTo = useMemo<Ensemble[] | undefined>(
    () =>
      setsWithArtworks
        ?.filter((set) => !removedSets.some((removed) => removed.id === set.id))
        .concat(addedSets),
    [setsWithArtworks, addedSets, removedSets],
  );

  const availableSets = useMemo<Ensemble[] | undefined>(
    () =>
      allSets?.filter(
        (set) => !setsAddedTo?.some((added) => added.id === set.id),
      ),
    [allSets, setsAddedTo],
  );

  const hasLoaded = useMemo(
    () => !!allSets && !!setsWithArtworks,
    [allSets, setsWithArtworks],
  );

  const addToSet = (set: Ensemble) => {
    if (removedSets.some((removed) => removed.id === set.id)) {
      setRemovedSets((sets) => sets.filter((removed) => removed.id !== set.id));
      return;
    }
    setAddedSets((sets) => [...sets, set]);
  };

  const removeFromSet = (set: Ensemble) => {
    if (addedSets.some((added) => added.id === set.id)) {
      setAddedSets((sets) => sets.filter((added) => added.id !== set.id));
      return;
    }
    setRemovedSets((sets) => [...sets, set]);
  };

  const onSubmit = async () => {
    if (selectedArtworksIds.length === 0) {
      return;
    }
    const promises = [];
    if (addedSets.length > 0) {
      promises.push(
        mutateAddArtworksToEnsembles({
          ensembleIds: addedSets.map((set) => set.id),
          artworkIds: selectedArtworksIds,
        }),
      );
    }
    if (removedSets.length > 0) {
      promises.push(
        mutateRemoveArtworksFromEnsembles({
          ensembleIds: removedSets.map((set) => set.id),
          artworkIds: selectedArtworksIds,
        }),
      );
    }
    try {
      await Promise.all(promises);
      setAddedSets([]);
      setRemovedSets([]);
      // Close modal
      resetSelectedArtworks();
      toast({
        title: t("success"),
        description: t("recordCreationSuccess"),
        duration: 3000,
        variant: "success",
      });
    } catch (_) {
      toast({
        title: t("failure"),
        description: t("recordCreationFailure"),
        duration: 3000,
        variant: "error",
      });
    }
  };

  const handleNewSetSubmit = async (data: NewArtworkSetFormValues) => {
    if (selectedArtworksIds.length === 0) return;
    // new set will automatically be added by refetching
    await mutateCreateTemporarySetWithArtworks({
      ensemblesDescriptionNomEnsemble: data.ensemblesDescriptionNomEnsemble,
      ensemblesDescriptionNotesEnsemble: data.ensemblesDescriptionNotesEnsemble,
      visibility: data.visibility,
      artworksIds: selectedArtworksIds,
    });
    setNewSetDialogOpen(false);
  };

  return (
    <StyledDrawer
      title={
        <div className="flex flex-row flex-1 items-center justify-between gap-3">
          <span>{t("addKey")}</span>
          <Button
            className="flex flex-row items-center gap-1"
            onClick={() => setNewSetDialogOpen(true)}
            disabled={disabled}
          >
            <Plus />
            {t("createNewEnsemble")}
          </Button>
        </div>
      }
      trigger={
        !open && !onOpenChange ? (
          <Button className="flex flex-row gap-2 items-center font-semibold text-sm">
            <Plus /> {t("addKey")}
          </Button>
        ) : undefined
      }
      footer={
        <CustomDialogFooter>
          <Button
            variant="outline"
            className="flex flex-1 bg-white hover:bg-gray-50 hover:text-black"
            onClick={() => resetSelectedArtworks()}
          >
            {t("actionCancel")}
          </Button>
          <Button className="flex flex-1" onClick={() => onSubmit()} disabled={disabled}>
            {t("addToEnsemble")}
          </Button>
        </CustomDialogFooter>
      }
      sheetProps={{
        open,
        onOpenChange: (open) => {
          onOpenChange?.(open);
          if (!open) {
            resetSelectedArtworks();
          }
        },
      }}
      // TODO: dynamic width
      className="min-w-[1000px] max-w-[90%]"
    >
      <div className="flex flex-col gap-4">
        <Label className="font-semibold text-base">
          {t("ensembleItemInEnsembleTemp")}
        </Label>

        {setsAddedTo && hasLoaded && (
          <ArtworkSetTable
            data={setsAddedTo}
            id={setsWithArtworksId}
            mode="remove"
            onItemAction={removeFromSet}
          />
        )}

        <Separator />

        <div className="flex flex-row items-center space-between gap-4">
          <Label className="font-semibold text-base flex flex-1">
            {t("ensembleSelectEnsembleTemp")}
          </Label>
          <div className="relative flex flex-1">
            <Input
              placeholder={t("search")}
              className="flex flex-1 pl-4"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Search
              size={16}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>

        {availableSets && hasLoaded && (
          <ArtworkSetTable
            data={availableSets}
            id={allSetsId}
            mode="add"
            onItemAction={addToSet}
            disabled={disabled}
          />
        )}
      </div>

      <NewArtworkSetModal
        disabled={disabled}
        open={newSetDialogOpen}
        onOpenChange={setNewSetDialogOpen}
        onSubmit={handleNewSetSubmit}
      />
    </StyledDrawer>
  );
};

export default CreateArtworkSet;
